import { Icon, IconButton, Skeleton, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { FC } from 'react';
import { DeviceType, useBreakpoint } from '../../hooks/useBreakpoint';

export type CfKebabButtonVariants = 'page' | 'row' | 'card' | 'list-item';

export type CfKebabButtonProps =
  | {
      onClick?: (e: HTMLButtonElement) => void;
      variant: CfKebabButtonVariants;
      dataTestId?: string;
      sx?: SxProps<Theme>;
      iconSx?: SxProps<Theme>;
      loadingPlaceholder?: never;
    }
  | {
      variant: CfKebabButtonVariants;
      dataTestId?: string;
      sx?: SxProps<Theme>;
      iconSx?: SxProps<Theme>;
      loadingPlaceholder: true;
    };

/**
 * @param props.onClick - handler that fires on 'enter' key press and mouse click
 * @param props.variant - adjusts button size based upon use-case. Augment as new use cases are introduced. Varaints are responsive
 * @param props.dataTestId - Test id of the button
 * @param props.sx - allows for adding margin to the button. Avoid overriding the default styles (sizing, padding, etc.)
 * @param props.iconSx - Additional styling for the icon
 * @param props.loadingPlaceholder - Boolean indicating if the loading placeholder should be shown
 */
export const CfKebabButton: FC<CfKebabButtonProps> = (props: CfKebabButtonProps) => {
  const deviceType = useBreakpoint().deviceType;
  const buttonSize = deviceType === DeviceType.Desktop ? 'small' : 'medium';
  let size: 'small' | 'medium' | undefined;
  switch (props.variant) {
    case 'page':
      size = 'medium';
      break;
    case 'row':
      size = deviceType === DeviceType.Desktop ? 'small' : 'medium';
      break;
    case 'card':
    case 'list-item':
    default:
      size = 'small';
  }

  if (props.loadingPlaceholder) {
    return (
      <Skeleton variant='circular'>
        <IconButton
          size={size}
          sx={{ height: { xs: 40, xl: 32 }, width: { xs: 40, xl: 32 }, ...props.sx }}
          data-testid={props.dataTestId}
        >
          <Icon className='fa-ellipsis-v' fontSize={size} />
        </IconButton>
      </Skeleton>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation?.();
    props.onClick?.(e.currentTarget);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    e?.stopPropagation?.();
    if (e.key.toLowerCase() == 'enter') {
      e.preventDefault();
      props.onClick?.(e.currentTarget);
    }
  };

  return (
    <IconButton
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      size={buttonSize}
      sx={{ height: { xs: 40, xl: 32 }, width: { xs: 40, xl: 32 }, ...props.sx }}
      data-testid={props.dataTestId}
    >
      <Icon
        className='fa-ellipsis-v'
        fontSize={size}
        sx={{ color: (theme) => theme.palette.coolGrey[600], ...props.iconSx }}
      />
    </IconButton>
  );
};
